import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface AudioPlayerContextValue {
  playingId: string | null;
  endedId: string | null;
  isPlaying: boolean;
  play: (url: string, id: string) => void;
  pause: () => void;
  getAudio: () => HTMLAudioElement | null;
  getAudioForUrl: (url: string) => HTMLAudioElement | null;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue>({
  playingId: null,
  endedId: null,
  isPlaying: false,
  play: () => {},
  pause: () => {},
  getAudio: () => null,
  getAudioForUrl: () => null,
});

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [endedId, setEndedId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Cache keeps paused audio elements alive (preserves currentTime)
  const audioCacheRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const activeUrlRef = useRef<string | null>(null);
  const playingIdRef = useRef<string | null>(null);

  useEffect(() => {
    playingIdRef.current = playingId;
  }, [playingId]);

  const getAudio = useCallback(() => activeAudioRef.current, []);
  const getAudioForUrl = useCallback(
    (url: string) => audioCacheRef.current.get(url) ?? null,
    [],
  );

  const pause = useCallback(() => {
    activeAudioRef.current?.pause();
  }, []);

  const play = useCallback((url: string, id: string) => {
    // Toggle: same message already playing → pause it
    if (
      playingIdRef.current === id &&
      activeAudioRef.current &&
      !activeAudioRef.current.paused
    ) {
      activeAudioRef.current.pause();
      return;
    }

    // Pause the currently active audio — keep it in the cache so it resumes later
    if (activeAudioRef.current && !activeAudioRef.current.paused) {
      activeAudioRef.current.pause();
    }

    // Get existing cached element (preserves currentTime) or create a new one
    let audio = audioCacheRef.current.get(url);
    if (!audio) {
      audio = new Audio(url);
      audioCacheRef.current.set(url, audio);
    }

    // Re-bind handlers every time we start playing to capture the current id
    audio.onended = () => {
      setEndedId(id);
      setPlayingId(null);
      setIsPlaying(false);
      playingIdRef.current = null;
      activeAudioRef.current = null;
      activeUrlRef.current = null;
      audioCacheRef.current.delete(url); // completed — evict from cache
    };
    audio.onpause = () => {
      // Only update state if this audio is still the active one
      if (activeUrlRef.current === url) setIsPlaying(false);
    };
    audio.onplay = () => {
      if (activeUrlRef.current === url) setIsPlaying(true);
    };

    activeAudioRef.current = audio;
    activeUrlRef.current = url;

    audio.play().catch((err) => {
      console.error("[AudioPlayerContext] play error:", err);
    });

    setPlayingId(id);
    setIsPlaying(true);
    playingIdRef.current = id;
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{ playingId, endedId, isPlaying, play, pause, getAudio, getAudioForUrl }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayerContext = () => useContext(AudioPlayerContext);
